import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Input, Button, ButtonColor } from '@swivl-mono/ui';
import { TableProfileCollectData, transformProfileCollectForTable } from '../Helpers/DataHelpers';
import { exportProfileCollectToCSV } from '../Helpers/CSVExportHelpers';
import { GenericTableRowInterface, UserPropKey, UserVarKey } from '@swivl-mono/types';
import { UsersModel } from '../../../Models/Users/Users.Model';
import UserVarsModel from '../../../Models/Variables/UserVars.Model';
import './ViewAllProfileCollect.scss';

interface ViewAllProfileCollectProps {
  rawProfileCollectData: GenericTableRowInterface[];
  onViewLess: () => void;
  locationNames: { [key: number]: string };
  startDay: string;
  endDay: string;
}

export const ViewAllProfileCollect: React.FC<ViewAllProfileCollectProps> = ({
  rawProfileCollectData,
  onViewLess,
  locationNames,
  startDay: _startDay,
  endDay: _endDay
}) => {
  const [tableData, setTableData] = useState<TableProfileCollectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<keyof TableProfileCollectData>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [hasUserSorted, setHasUserSorted] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Get user data from UsersModel
  const { users } = UsersModel.useState();
  const { vars } = UserVarsModel.useState();

  // Load user data when component mounts
  useEffect(() => {
    if (rawProfileCollectData && rawProfileCollectData.length > 0) {
      // Get unique user IDs
      const userIds = [...new Set(rawProfileCollectData.map(item => item.userId).filter(Boolean))] as number[];

      // Load users if we have IDs
      if (userIds.length > 0) {
        UsersModel.loadUsersByIds(userIds);

        // Also load user variables for each user
        userIds.forEach(userId => {
          UserVarsModel.loadForUser(userId);
        });
      }
    }
  }, [rawProfileCollectData]);

  // Transform data when we have profile collect data and location names
  const enhancedTableData = useMemo(() => {
    if (!rawProfileCollectData || !locationNames) {
      return [];
    }

    const baseData = transformProfileCollectForTable(rawProfileCollectData, locationNames);

    // First, enhance with user data
    const enhancedData = baseData.map(item => {
      const user = users?.[item.userId];
      const userVars = vars?.[item.userId] || {};

      return {
        ...item,
        name: user?.name || userVars[UserPropKey.Name] || '',
        email: user?.email || userVars[UserPropKey.Email] || '',
        phone: user?.phoneNumber || userVars[UserPropKey.PhoneNumber] || '',
        crmStatus: userVars['CRMStatus'] || 'Pending',
        notes: userVars['Notes'] || ''
      };
    });

    // Filter out rows without contact info (name, email, and phone all empty)
    const filteredData = enhancedData.filter(item =>
      item.name.trim() !== '' || item.email.trim() !== '' || item.phone.trim() !== ''
    );

    // Deduplicate by userId, keeping the most recent row for each user
    const userMap = new Map<number, TableProfileCollectData>();
    filteredData.forEach(item => {
      const existingItem = userMap.get(item.userId);
      if (!existingItem || item.updatedAt > existingItem.updatedAt) {
        userMap.set(item.userId, item);
      }
    });

    return Array.from(userMap.values());
  }, [rawProfileCollectData, locationNames, users, vars]);

  // Filter and sort data based on search query and sort settings
  const filteredAndSortedTableData = useMemo(() => {
    // First filter by search query
    let filtered = enhancedTableData;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = enhancedTableData.filter(row =>
        row.locationName.toLowerCase().includes(query) ||
        row.funnel.toLowerCase().includes(query) ||
        row.name.toLowerCase().includes(query) ||
        row.email.toLowerCase().includes(query) ||
        row.phone.toLowerCase().includes(query) ||
        row.crmStatus.toLowerCase().includes(query) ||
        row.notes.toLowerCase().includes(query) ||
        row.updatedAt.toLocaleDateString().toLowerCase().includes(query)
      );
    }

    // Then sort the filtered data
    return [...filtered].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // Handle different data types
      let comparison = 0;
      if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        // Fallback to string comparison
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [enhancedTableData, searchQuery, sortField, sortDirection]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedTableData.length / pageSize);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedTableData.slice(startIndex, endIndex);
  }, [filteredAndSortedTableData, currentPage, pageSize]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (enhancedTableData.length > 0) {
      setTableData(paginatedData);
      setLoading(false);
    } else if (rawProfileCollectData && rawProfileCollectData.length === 0) {
      setLoading(false);
    }
  }, [enhancedTableData, paginatedData, rawProfileCollectData]);

  const handleSort = (field: keyof TableProfileCollectData) => {
    setHasUserSorted(true);
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExport = () => {
    exportProfileCollectToCSV(filteredAndSortedTableData);
  };

  const handleRowUpdate = async (userId: number, field: string, value: string, shouldSave: boolean = false) => {
    // Update local state immediately
    setTableData(prev => prev.map(row =>
      row.userId === userId ? { ...row, [field]: value } : row
    ));

    // Save to user variables if shouldSave is true (for status auto-save or notes manual save)
    if (shouldSave) {
      try {
        const currentUserVars = vars?.[userId] || {};
        await UserVarsModel.saveUserVars(userId, {
          ...currentUserVars,
          [field]: value
        });
      } catch (error) {
        console.error('Failed to save user variable:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="ViewAllProfileCollect">
        <div className="loadingContainer">
          <Spinner />
          <div style={{ marginTop: '20px', fontSize: '16px', color: '#666' }}>
            Loading profile data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ViewAllProfileCollect">
      <div className="header">
        <Link to="/" className="backButton">
          ← Back
        </Link>
        <div className="headerActions">
          <div className="exportButtonWrapper">
            <Button
              color={ButtonColor.WHITE}
              onClick={handleExport}
              disabled={tableData.length === 0}
            >
              Export CSV
            </Button>
          </div>
          <div className="searchInputWrapper">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              isSearch={true}
              isRound={true}
            />
          </div>
          <span className="recordCount">
            {searchQuery ?
              `${filteredAndSortedTableData.length} of ${enhancedTableData.length} records` :
              `${enhancedTableData.length} records`}
          </span>
        </div>
      </div>

      <div className="tableContainer">
        <table className="profileCollectTable">
          <thead>
            <tr>
              <SortableHeader field="crmStatus" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Status
              </SortableHeader>
              <SortableHeader field="notes" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Notes
              </SortableHeader>
              <SortableHeader field="name" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Name
              </SortableHeader>
              <SortableHeader field="email" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Email
              </SortableHeader>
              <SortableHeader field="phone" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Phone
              </SortableHeader>
              <SortableHeader field="funnel" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Funnel
              </SortableHeader>
              <SortableHeader field="locationName" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Location
              </SortableHeader>
              <th>Conversation</th>
              <SortableHeader field="updatedAt" currentSort={sortField} direction={sortDirection} onSort={handleSort} hasUserSorted={hasUserSorted}>
                Updated
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={9} className="noData">
                  No profile data available with the current filters.
                </td>
              </tr>
            ) : (
              tableData.map(row => (
                <TableRow
                  key={row.id}
                  data={row}
                  onUpdate={handleRowUpdate}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="paginationContainer">
          <div className="paginationControls">
            <Button
              color={ButtonColor.BLACK}
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              color={ButtonColor.BLACK}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            <span className="pageInfo">
              Page {currentPage} of {totalPages}
            </span>

            <Button
              color={ButtonColor.BLACK}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              color={ButtonColor.BLACK}
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>

          <div className="pageSizeSelector">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="pageSizeDropdown"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span> records per page</span>
          </div>
        </div>
      )}
    </div>
  );
};

interface TableRowProps {
  data: TableProfileCollectData;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const TableRow: React.FC<TableRowProps> = ({ data, onUpdate }) => {
  return (
    <tr>
      <td className="statusCell">
        <StatusDropdown
          value={data.crmStatus}
          userId={data.userId}
          onUpdate={onUpdate}
        />
      </td>
      <td className="notesCell">
        <NotesField
          value={data.notes}
          userId={data.userId}
          onUpdate={onUpdate}
        />
      </td>
      <td className="nameCell">
        <span className="value">{data.name || '-'}</span>
      </td>
      <td className="emailCell">
        <span className="value">{data.email || '-'}</span>
      </td>
      <td className="phoneCell">
        <span className="value">{data.phone || '-'}</span>
      </td>
      <td className="funnelCell">
        {data.funnel}
      </td>
      <td className="locationCell">
        {data.locationName}
      </td>
      <td className="conversationCell">
        <a
          href={data.conversationLink}
          target="_blank"
          rel="noopener noreferrer"
          className="conversationLink"
          title="View Conversation"
        >
          💬
        </a>
      </td>
      <td className="updatedCell">
        {data.updatedAt.toLocaleDateString()} {data.updatedAt.toLocaleTimeString()}
      </td>
    </tr>
  );
};

interface StatusDropdownProps {
  value: string;
  userId: number;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({ value, userId, onUpdate }) => {
  const statusOptions = [
    { value: 'Pending', label: '⏳ Pending' },
    { value: 'In Progress', label: '🔄 In Progress' },
    { value: 'On Hold', label: '⏸️ On Hold' },
    { value: 'Call Back', label: '📞 Call Back' },
    { value: 'Completed', label: '✅ Completed' }
  ];

  return (
    <select
      value={value}
      onChange={(e) => onUpdate(userId, 'CRMStatus', e.target.value, true)} // Auto-save status changes
      className="statusDropdown"
    >
      {statusOptions.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

interface NotesFieldProps {
  value: string;
  userId: number;
  onUpdate: (userId: number, field: string, value: string, shouldSave?: boolean) => void;
}

const NotesField: React.FC<NotesFieldProps> = ({ value, userId, onUpdate }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local value when props change (for real-time updates from other users)
  useEffect(() => {
    setLocalValue(value);
    setHasChanges(false);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalValue(e.target.value);
    setHasChanges(e.target.value !== value);
  };

  const handleSave = () => {
    if (hasChanges) {
      onUpdate(userId, 'Notes', localValue, true); // Save to database
      setHasChanges(false);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalValue(value); // Reset to original value
    setHasChanges(false);
    setIsEditing(false);
  };

  return (
    <div className="notesFieldContainer">
      <textarea
        value={localValue}
        onChange={handleChange}
        onFocus={() => setIsEditing(true)}
        className={`notesField ${isEditing ? 'editing' : ''}`}
        rows={2}
        placeholder="Add notes..."
      />
      {isEditing && (
        <div className="notesActions">
          <button
            className="saveButton"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save
          </button>
          <button
            className="cancelButton"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

interface SortableHeaderProps {
  field: keyof TableProfileCollectData;
  currentSort: keyof TableProfileCollectData;
  direction: 'asc' | 'desc';
  onSort: (field: keyof TableProfileCollectData) => void;
  hasUserSorted: boolean;
  children: React.ReactNode;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, currentSort, direction, onSort, hasUserSorted, children }) => {
  const isActive = hasUserSorted && currentSort === field;

  return (
    <th
      className={`sortable ${isActive ? 'active' : ''}`}
      onClick={() => onSort(field)}
    >
      {children}
      <span className="sortIcon">
        {isActive ? (direction === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
};
